/** Builds the chat cards each widget shows from Activity items, Active chats and pins. */
import type { TFunction } from 'i18next';
import type { CrmAccount } from '../crm/crmAccess';
import type { ActivityItem, ActivityKind } from './activity';
import { chatKey, isFanReplied, type ActiveChat, type ChatStateRow } from './chats';
import { accountColour, type ColourMode } from './colour';
import type { FanInfo, PinRow } from './api';
import { feedKey, money, type FeedItem, type FeedSettings } from './feed';
import type { CardItem, CardKind } from './widgets/ChatCollection';

export const KIND_META: Record<ActivityKind, { icon: string; hue: number }> = {
  message: { icon: 'message-circle', hue: 295 },
  ppv: { icon: 'lock-open', hue: 155 },
  tip: { icon: 'hand-coins', hue: 80 },
  handoff: { icon: 'hand', hue: 25 },
  hot: { icon: 'flame', hue: 355 },
  list: { icon: 'eye', hue: 225 },
  subscriber: { icon: 'user-plus', hue: 190 },
};

/** "now", "4m", "2h", "3d". */
export function ago(iso: string | null | undefined, nowMs: number): string {
  const t = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((nowMs - t) / 1000));
  if (s < 45) return 'now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

function bpOf(raw: unknown): number | null {
  const n = raw == null || raw === '' ? NaN : Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.min(5, Math.round(n))) : null;
}

function amountLabel(p: { amount?: number | null; amount_label?: string | null }): string | undefined {
  if (p.amount != null && Number.isFinite(Number(p.amount))) return `$${Number(p.amount).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return p.amount_label || undefined;
}

export interface CardContext {
  accounts: Map<string, CrmAccount>;
  nowMs: number;
  t: TFunction;
  tc: TFunction;
  colourMode?: ColourMode;
  /** Names/avatars looked up for chats whose rows came without them (key: chatKey). */
  fans?: Map<string, FanInfo>;
}

function base(ctx: CardContext, accountId: string, fanId: string, fanName: string | null | undefined, fanAvatar: string | null | undefined): Omit<CardItem, 'key' | 'sub' | 'subIcon' | 'time'> {
  const account = ctx.accounts.get(accountId);
  const c = accountColour(accountId, ctx.colourMode);
  const found = ctx.fans?.get(chatKey(accountId, fanId));
  return {
    accountId,
    fanId,
    fanName: fanName || found?.name || found?.username || fanId,
    fanAvatar: fanAvatar || found?.avatar || null,
    modelName: account?.profile_name || account?.username || accountId,
    modelAvatar: account?.avatar_url || null,
    modelHandle: account?.username ? `@${account.username}` : undefined,
    accent: c.accent,
    tint: c.tint,
    bp: null,
  };
}

function activityText(item: ActivityItem, ctx: CardContext): string {
  const p = item.event.payload;
  const { t, tc } = ctx;
  switch (item.kind) {
    case 'message':
      return p.snippet || (p.has_media ? t('activity.text.media') : t('activity.text.message'));
    case 'ppv':
      return t('activity.text.ppv');
    case 'tip':
      return p.snippet ? `“${p.snippet}”` : t('activity.text.noMessage');
    case 'handoff':
      return tc(`reasons.${p.reason}`, { defaultValue: String(p.reason || '').replace(/_/g, ' ') });
    case 'hot':
      return item.event.kind === 'hot_enter'
        ? t('activity.text.hotEnter', { phase: String(p.phase || '').replace(/_/g, ' ') })
        : p.snippet || t('activity.text.hotMessage');
    case 'list':
      return item.lists === 'ignored' ? t('activity.text.listIgnored', { lists: (p.lists || []).join(', ') }) : t('activity.text.list', { lists: (item.lists || p.lists || []).join(', ') });
    case 'subscriber':
      return p.via === 'follow' ? t('activity.text.follower') : t('activity.text.subscriber');
    default:
      return '';
  }
}

export function activityCard(item: ActivityItem, index: number, ctx: CardContext, onDismiss: (id: string) => void): CardItem {
  const ev = item.event;
  const meta = KIND_META[item.kind];
  // A Fansly follow is a New subscriber, labelled as what it is (ADR 0045).
  const label =
    item.kind === 'list' && item.lists === 'ignored'
      ? ctx.t('activity.aiIgnoredLabel')
      : item.kind === 'subscriber' && ev.payload.via === 'follow'
        ? ctx.t('activity.kinds.follower')
        : ctx.t(`activity.kinds.${item.kind}`);
  const kind: CardKind = { label, icon: meta.icon, hue: meta.hue, amount: item.kind === 'ppv' || item.kind === 'tip' ? amountLabel(ev.payload) : undefined };
  return {
    ...base(ctx, ev.account_id, ev.fan_id, ev.payload.fan_name, ev.payload.fan_avatar),
    key: `a:${item.id}`,
    bp: bpOf(ev.payload.buying_power),
    kind,
    sub: activityText(item, ctx),
    subIcon: 'quote',
    time: ago(ev.occurred_at, ctx.nowMs),
    dim: index > 8 ? 0.35 : index > 7 ? 0.6 : 1,
    action: { icon: 'x', tip: ctx.t('activity.dismiss'), run: () => onDismiss(item.id) },
  };
}

export function activeCards(groups: { replied: ActiveChat[]; waiting: ActiveChat[] }, ctx: CardContext): CardItem[] {
  const replied = groups.replied.map((c, i) => ({
    ...base(ctx, c.account_id, c.fan_id, c.fan_name, c.fan_avatar),
    key: `c:${chatKey(c.account_id, c.fan_id)}`,
    sub: c.last_fan_snippet || ctx.t('active.fanWrote'),
    subIcon: 'arrow-down-left',
    subTone: 'strong' as const,
    time: ago(c.last_fan_msg_at, ctx.nowMs),
    flag: ctx.t('active.replied'),
    group: i === 0 ? { label: ctx.t('active.repliedGroup', { count: groups.replied.length }), accent: true } : null,
  }));
  const waiting = groups.waiting.map((c, i) => ({
    ...base(ctx, c.account_id, c.fan_id, c.fan_name, c.fan_avatar),
    key: `c:${chatKey(c.account_id, c.fan_id)}`,
    sub: c.last_out_by === 'ai' ? ctx.t('active.aiWrote') : ctx.t('active.youWrote'),
    subIcon: 'arrow-up-right',
    time: ago(c.my_last_at, ctx.nowMs),
    group: i === 0 ? { label: ctx.t('active.waitingGroup', { count: groups.waiting.length }) } : null,
  }));
  return [...replied, ...waiting];
}

export function pinCards(pins: PinRow[], states: Map<string, ChatStateRow>, ctx: CardContext, onUnpin: (p: PinRow) => void): CardItem[] {
  return pins.map((p) => {
    const live = states.get(chatKey(p.account_id, p.fan_id));
    const st = live ?? { last_fan_msg_at: p.last_fan_msg_at ?? null, last_out_at: p.last_out_at ?? null, last_fan_snippet: p.last_fan_snippet ?? null };
    const replied = isFanReplied(st);
    return {
      ...base(ctx, p.account_id, p.fan_id, live?.fan_name || p.fan_name || p.fan_username, live?.fan_avatar || p.fan_avatar),
      key: `p:${chatKey(p.account_id, p.fan_id)}`,
      fanUsername: p.fan_username,
      sub: replied ? st.last_fan_snippet || ctx.t('active.fanWrote') : st.last_out_at ? ctx.t('pinned.waiting') : ctx.t('pinned.noActivity'),
      subIcon: replied ? 'arrow-down-left' : 'arrow-up-right',
      subTone: replied ? ('strong' as const) : ('muted' as const),
      time: ago(replied ? st.last_fan_msg_at : st.last_out_at, ctx.nowMs),
      flag: replied ? ctx.t('pinned.new') : null,
      action: { icon: 'x', tip: ctx.t('pinned.unpin'), run: () => onUnpin(p) },
    };
  });
}

/** "Spent $340 · no chat today" / "VIP list · 4 dots" (handoff: card content per design). */
export function feedWhy(item: FeedItem, s: FeedSettings, ctx: CardContext): string {
  const { t } = ctx;
  const noChat = t(`feed.why.noChat.${s.chatPeriod}`);
  if (item.source === 'lists') {
    const name = item.list_ids.map((id) => s.listNames[`${item.account_id}:${id}`]).find(Boolean) || t('feed.why.aList');
    const tail = item.bp != null ? t('feed.why.dots', { count: item.bp }) : item.spent > 0 ? t('feed.why.spent', { amount: money(item.spent) }) : noChat;
    return `${t('feed.why.list', { name })} · ${tail}`;
  }
  return `${t('feed.why.spent', { amount: money(item.spent) })} · ${noChat}`;
}

export function feedCards(items: FeedItem[], s: FeedSettings, ctx: CardContext, onSkip: (item: FeedItem) => void): CardItem[] {
  const { t } = ctx;
  return items.map((it) => {
    const who = it.last_chat_by ? t(`feed.by.${it.last_chat_by}`) : '';
    const lastBuy = it.last_purchase_at ? `${money(it.last_purchase_amount ?? it.spent)} · ${t('feed.ago', { time: ago(it.last_purchase_at, ctx.nowMs) })}` : t('feed.never');
    const lastChat = it.last_chat_at ? `${t('feed.ago', { time: ago(it.last_chat_at, ctx.nowMs) })}${who ? ` · ${who}` : ''}` : t('feed.never');
    return {
      ...base(ctx, it.account_id, it.fan_id, it.fan_name || it.fan_username, it.fan_avatar),
      key: `f:${feedKey(it)}`,
      fanUsername: it.fan_username,
      bp: it.bp,
      spent: it.spent > 0 ? money(it.spent) : null,
      sub: feedWhy(it, s, ctx),
      subIcon: 'sparkles',
      subTone: 'accent' as const,
      time: it.last_chat_at ? ago(it.last_chat_at, ctx.nowMs) : '',
      stats: [
        { label: t('feed.lastPurchase'), value: lastBuy },
        { label: t('feed.lastChatted'), value: lastChat },
      ],
      action: { icon: 'x', tip: t('feed.skipHint', { hours: s.snoozeHours }), run: () => onSkip(it) },
    };
  });
}
